'use client';

import { useSession } from 'next-auth/react';
import { useSearchParams } from 'next/navigation';
import { useContext, useEffect, useMemo, useState } from 'react';

import { FundingKey, JourneyType, ProductModel, ProductType, ResourceType } from '@bytel/leviator';
import { CartContext } from '@bytel/leviator/react';
import {
    Container,
    DirectionEnum,
    FlexBox,
    GapSize,
    Section,
    Title,
    TitleLevels,
    TitleMarkup,
    TrilogyColor,
    TypographyAlign,
} from '@bytel/trilogy-react';

import { getOfferWallData } from '@services/categories';
import { getQuotePayload } from '@services/products';

import { CategoryPlanType, MobilePlanType } from '@app-types/plan';
import { PriceFundingsDetails } from '@app-types/product';

import { Faq } from '@components/faq';
import { Legals } from '@components/legals';
import { LetterVariant } from '@components/letter-variant';
import { SSIBlock } from '@components/ssi-block';
import { TriggerLogin } from '@components/trigger-login';

import { useMarketings } from '@hooks/use-marketings';
import { useScrollToHash } from '@hooks/use-scroll-to-hash';

import { PremiumSubCategories, SimoSubCategories } from '@helpers/offer-wall';
import { PAGE_SLUG, partials } from '@helpers/partials';
import { paths } from '@helpers/path';
import { LockedPlanValue, getBlockedPlans } from '@helpers/plan-lock';
import { getNeededElementGencodes } from '@helpers/product';
import { sendPlanDetailsTracking } from '@helpers/walls';

import { MarketingType, OPTION_LOCKED_ID, PlanRange, PlanSubTypeId, PlanTypeId } from '@constants/plan';

import { getSimulatedPhone, offerWallPageTracking } from '@offer-wall/helpers';
import { useQuery } from '@tanstack/react-query';

import { ModalPlanDetailsContainer } from './modal-plan-details-container';
import { OfferCategoryListing } from './offer-category-list';
import { OfferSkeleton } from './offer-skeleton';
import { UpgradePlanModal } from './upgrade-plan-modal';

interface OfferWallProps {
    categories: CategoryPlanType[];
}

export function OfferWall({ categories: _categories }: Readonly<OfferWallProps>) {
    const {
        cart,
        isLoading: isCartLoading,
        addProductsToCartMutation,
        addResourceToProductMutation,
        setFundingModeMutation,
    } = useContext(CartContext);
    const { data: session } = useSession();
    const searchParams = useSearchParams();
    const [marketingDiscounts] = useMarketings();

    const [selectedOffer, setSelectedOffer] = useState<MobilePlanType>();
    const [offerSelectedForAddToCart, setOfferSelectedForAddToCart] = useState<MobilePlanType>();
    const [isModalOpened, setIsModalOpened] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const journey = useMemo(() => {
        return cart?.getJourneyOfType(JourneyType.MOBILE_ACQUISITION);
    }, [cart]);

    const phoneInCart = useMemo(() => {
        return journey?.getProductsByType(ProductType.TERMINAL)?.[0];
    }, [journey]);

    const currentSubType = useMemo(() => {
        return journey?.getProductsByType(ProductType.MAIN_MOBILE_OFFER)?.[0]?.metadata?.subType ===
            PlanSubTypeId.SMART_CHANGE
            ? PlanSubTypeId.SMART_CHANGE
            : PlanSubTypeId.CLASSIC;
    }, [journey]);

    const currentCategory = useMemo(() => {
        return Boolean(phoneInCart) ? PlanTypeId.PREMIUM : PlanTypeId.SIMO;
    }, [phoneInCart]);

    const categoriesQuery = useQuery({
        queryKey: ['offerWallCategories', searchParams.get('clientbbox'), phoneInCart?.gencode, session],
        queryFn: async () => {
            const quotes = getQuotePayload(
                searchParams.get('clientbbox') !== 'false',
                phoneInCart?.gencode,
                !!session?.user,
            );

            return getOfferWallData(
                [...PremiumSubCategories, ...SimoSubCategories],
                { cart: { quotes } },
                session,
                Boolean(phoneInCart?.gencode),
            );
        },
        initialData: _categories,
    });

    const simulatedPhone: { phone: ProductModel; details?: PriceFundingsDetails } | undefined = useMemo(() => {
        if (!phoneInCart?.gencode) {
            return undefined;
        }

        return {
            phone: phoneInCart,
            details: getSimulatedPhone(categoriesQuery.data?.[0]?.products?.[0]?.simulatedCart, phoneInCart.gencode)
                ?.details,
        };
    }, [categoriesQuery.data, phoneInCart?.gencode]);

    const filteredCategories = useMemo(() => {
        return categoriesQuery.data.reduce((acc: CategoryPlanType[], category: CategoryPlanType) => {
            if (!category.code.includes(currentCategory)) return acc;

            const filteredProducts = category.products.filter((product) => product.subTypeId === currentSubType);

            if (filteredProducts.length > 0) {
                acc.push({
                    ...category,
                    products: filteredProducts,
                });
            }

            return acc;
        }, []);
    }, [categoriesQuery.data, currentCategory, currentSubType]);

    useScrollToHash(!isCartLoading && !categoriesQuery.isFetching && filteredCategories.length > 0);

    const onSelectedOffer = (offer: MobilePlanType) => {
        sendPlanDetailsTracking(offer);
        setSelectedOffer(offer);
    };

    const onCloseAddToCartModal = () => {
        setOfferSelectedForAddToCart(undefined);
        setIsModalOpened(false);
    };

    const onAddToCart = async (
        selectedOffer: MobilePlanType,
        fundingKey?: FundingKey,
        fromUpgradeModal?: boolean,
        redirectToSmartphonesWall?: boolean,
    ) => {
        if (fromUpgradeModal) {
            setIsLoading(true);
        }
        setSelectedOffer(undefined);
        setOfferSelectedForAddToCart(selectedOffer);

        // Simulated portability is auto-selected by default, but only on a first add.
        //
        // The offer wall has no portability toggle of its own: that choice is only made on the
        // product detail page (PromoPortability). When a plan is already in the cart, the user may
        // have explicitly deselected portability there, so re-adding the PORTABILITY_SIMULATED
        // resource here would silently override that choice. We therefore only auto-add it when the
        // cart has no plan yet.
        //
        // Trade-off: switching to a different plan from the wall (a plan is already in cart) will
        // not re-default portability to "on", even if the new plan carries a portability promo. The
        // user can re-enable it from the product detail page. This is intentional — preserving an
        // explicit deselection takes priority over re-defaulting on a plan change.
        const hasPlanInCart = !!journey?.getPrincipalProduct();

        const autoAdd = getNeededElementGencodes(selectedOffer);

        // Handle locked plans option
        if (selectedOffer.locked === LockedPlanValue.LOCK_OPTIONAL) {
            const blockedsPlans = getBlockedPlans();
            if (blockedsPlans[selectedOffer.entityId!]) {
                autoAdd.push(OPTION_LOCKED_ID);
            }
        }

        // Handle mirrorPlans / UpgradePlanModal logic
        if (
            redirectToSmartphonesWall &&
            !fromUpgradeModal &&
            (selectedOffer.mirrorPlans?.swappy || selectedOffer.mirrorPlans?.fam)
        ) {
            setIsModalOpened(true);
            return;
        }

        const offerToAdd =
            redirectToSmartphonesWall && selectedOffer.mirrorPlans?.fam ? selectedOffer.mirrorPlans.fam : selectedOffer;

        await addProductsToCartMutation.mutateAsync({
            journey: JourneyType.MOBILE_ACQUISITION,
            gencodes: phoneInCart
                ? [offerToAdd.gencode, phoneInCart.gencode, ...autoAdd]
                : [offerToAdd.gencode, ...autoAdd],
        });

        if (fundingKey) {
            await setFundingModeMutation.mutateAsync({
                fundingKey,
                callbackUrl: `${window.location.origin}/tunnel/redirect`, // Unused in virtual cart
            });
        }

        setIsLoading(false);

        let redirectionPath: string;

        if (redirectToSmartphonesWall && selectedOffer.mirrorPlans?.fam) {
            redirectionPath = paths.phoneWall;
        } else if (selectedOffer.subTypeId === PlanSubTypeId.SMART_CHANGE) {
            redirectionPath = paths.phoneFilter(false, 'smart-change');
        } else if (
            selectedOffer.commercialRange === PlanRange.WITHOUT_MOBILE ||
            (selectedOffer.commercialRange === PlanRange.WITH_MOBILE && !!phoneInCart)
        ) {
            redirectionPath = paths.crossSell();
        } else {
            redirectionPath = paths.phoneWall;
        }

        if (
            !hasPlanInCart &&
            offerToAdd.details.discounts.some((discount) => discount.marketingTypes.includes(MarketingType.PORTABILITY))
        ) {
            await addResourceToProductMutation.mutateAsync({
                gencode: offerToAdd.gencode,
                type: ResourceType.PORTABILITY_SIMULATED,
            });
        }
        window.location.assign(redirectionPath);
    };

    useEffect(() => {
        if (isCartLoading) return;
        offerWallPageTracking(
            currentCategory === PlanTypeId.PREMIUM,
            false,
            Boolean(session?.user),
            marketingDiscounts.FOYER,
            Boolean(phoneInCart),
        );
    }, [session?.user?.sub, currentCategory, isCartLoading, phoneInCart]);

    return (
        <>
            <Section backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                <Container>
                    <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.FIVE}>
                        <div>
                            <Title markup={TitleMarkup.H1} level={TitleLevels.ONE} typo={TypographyAlign.TEXT_CENTERED}>
                                Les forfaits m<LetterVariant letter='o' />
                                bile Bouygues Telecom
                            </Title>
                            <SSIBlock url={partials.MARKETING.OFFER_WALL_SLIDER} />
                        </div>
                        {categoriesQuery.isFetching ? (
                            <OfferSkeleton />
                        ) : (
                            filteredCategories.map((category: CategoryPlanType) => (
                                <OfferCategoryListing
                                    key={category.id}
                                    category={category}
                                    openOfferDetails={onSelectedOffer}
                                    currentCategory={currentCategory}
                                    phoneInCart={simulatedPhone}
                                    isPhoneLoading={isCartLoading}
                                    onAddToCart={onAddToCart}
                                    isConnected={!!session?.user}
                                    isSmallPriceCategory={
                                        category.code === PlanTypeId.PREMIUM_MULTILIGNE ||
                                        category.code === PlanTypeId.SIMO_MULTILIGNE
                                    }
                                />
                            ))
                        )}
                    </FlexBox>
                    {selectedOffer && !isModalOpened && (
                        <ModalPlanDetailsContainer
                            selectedOffer={selectedOffer}
                            marketingDiscounts={marketingDiscounts}
                            onClose={() => setSelectedOffer(undefined)}
                        />
                    )}
                    <UpgradePlanModal
                        plan={offerSelectedForAddToCart}
                        modalOpened={isModalOpened}
                        onClose={onCloseAddToCartModal}
                        isLoading={isLoading}
                        onAdd={(withSmartphone) => {
                            if (offerSelectedForAddToCart) {
                                onAddToCart(offerSelectedForAddToCart, undefined, true, withSmartphone);
                            }
                        }}
                    />
                    <TriggerLogin />
                </Container>
            </Section>
            <Section backgroundColor={TrilogyColor.BACKGROUND}>
                <Container className='is-fullwidth'>
                    <SSIBlock url={partials.MARKETING.FAI_CONTENT} />
                    <SSIBlock url={partials.MARKETING.FAI_BOTTOM_CONTENT} />
                </Container>
            </Section>
            <Section backgroundColor={TrilogyColor.BACKGROUND}>
                <Container className='is-fullwidth'>
                    <Faq
                        title={
                            <>
                                Des questi
                                <LetterVariant letter='o' />
                                ns sur nos forfaits mobiles&nbsp;?
                                <br />
                                On vous répond&nbsp;!
                            </>
                        }
                        titleAlt='Des questions sur nos forfaits mobiles ? C\u2019est par ici !'
                        url={PAGE_SLUG.OFFER_WALL}
                    />
                </Container>
            </Section>
            <Section backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                <Container className='is-fullwidth'>
                    <Legals id='offer-legals' url={PAGE_SLUG.OFFER_WALL} />
                </Container>
            </Section>
        </>
    );
}
